import { useState } from "react";

import { faPaste } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { MUIUnifiedModal } from "shared/atoms/MUIUnifiedModal";

import { LengthOption } from "../SettingsSidebar/styles";
import type { TimelineActivity } from "../types";

interface PinExerciseModalProps {
  selectedActivityToPin: TimelineActivity;
  onClose: () => void;
  onPin: (activity: TimelineActivity, selectedDays: number[]) => void;
}

export const PinExerciseModal: React.FC<PinExerciseModalProps> = ({ selectedActivityToPin, onClose, onPin }) => {
  const { t } = useTranslation();
  const [selectedDays, setSelectedDays] = useState<Set<number>>(new Set(selectedActivityToPin.days || []));
  const availableDays = [
    { label: t("common.days_options.monday"), value: 1 },
    { label: t("common.days_options.tuesday"), value: 2 },
    { label: t("common.days_options.wednesday"), value: 3 },
    { label: t("common.days_options.thursday"), value: 4 },
    { label: t("common.days_options.friday"), value: 5 },
    { label: t("common.days_options.saturday"), value: 6 },
    { label: t("common.days_options.sunday"), value: 7 },
  ];

  const toggleDay = (day: number) => {
    setSelectedDays(prev => {
      const newSet = new Set(prev);
      if (newSet.has(day)) {
        newSet.delete(day);
      } else {
        newSet.add(day);
      }
      return newSet;
    });
  };

  const toggleAll = () => {
    if (selectedDays.size === availableDays.length) {
      setSelectedDays(new Set());
    } else {
      setSelectedDays(new Set(availableDays.map(day => day.value)));
    }
  };

  const handlePin = () => {
    onPin(selectedActivityToPin, Array.from(selectedDays));
    onClose();
  };

  return (
    <MUIUnifiedModal
      open
      onClose={onClose}
      width="max-content"
      title={
        <Stack direction="row" alignItems="center" gap={1.5} sx={{ color: "#0066cc" }}>
          <FontAwesomeIcon icon={faPaste} />
          <Typography variant="h4">{t("treatment.editor.pin_exercise.title")}</Typography>
        </Stack>
      }
    >
      <ExerciseInfo>
        {selectedActivityToPin.image_url && (
          <ExerciseImage src={selectedActivityToPin.image_url} alt={selectedActivityToPin.title} />
        )}
        <ExerciseDetails>
          <ExerciseName>{selectedActivityToPin.title}</ExerciseName>
          <ExerciseMeta>Level {selectedActivityToPin.level}</ExerciseMeta>
        </ExerciseDetails>
      </ExerciseInfo>

      <Section>
        <SectionHeader>
          <SectionTitle>{t("treatment.editor.pin_exercise.select_days")}</SectionTitle>
          <SelectAllButton onClick={toggleAll}>
            {selectedDays.size === availableDays.length
              ? t("treatment.editor.copy_exercise.deselect_all")
              : t("treatment.editor.copy_exercise.select_all")}
          </SelectAllButton>
        </SectionHeader>

        <DaysContainer>
          {availableDays.map(day => (
            <LengthOption key={day.value} $active={selectedDays.has(day.value)} onClick={() => toggleDay(day.value)}>
              {day.label}
            </LengthOption>
          ))}
        </DaysContainer>
      </Section>

      <Footer>
        <CancelButton onClick={onClose}>{t("buttons.cancel")}</CancelButton>
        <PasteButton onClick={handlePin} disabled={!selectedActivityToPin.pinned && selectedDays.size === 0}>
          <FontAwesomeIcon icon={faPaste} />
          {t("treatment.editor.pin_exercise.pin_to", { count: selectedDays.size })}
        </PasteButton>
      </Footer>
    </MUIUnifiedModal>
  );
};

const ExerciseInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  background-color: #f8f9fa;
  border-bottom: ${({ theme }) => theme.semantic.container.border};
`;

const ExerciseImage = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  object-fit: cover;
`;

const ExerciseDetails = styled.div`
  flex: 1;
`;

const ExerciseName = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: #1a2332;
`;

const ExerciseMeta = styled.div`
  font-size: 13px;
  color: #5c6f82;
  margin-top: 2px;
`;

const Section = styled.div`
  padding: 20px 24px;
  flex: 1;
  overflow-y: auto;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #1a2332;
  margin: 0;
`;

const SelectAllButton = styled.button`
  background: none;
  border: none;
  color: #0066cc;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background-color: #e6f2ff;
  }
`;

const DaysContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: ${({ theme }) => theme.semantic.container.border};
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background-color: #ffffff;
  border: ${({ theme }) => theme.semantic.container.border};
  border-radius: 6px;
  color: #5c6f82;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
    border-color: #c5d0dd;
  }
`;

const PasteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background-color: #0066cc;
  border: none;
  border-radius: 6px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #0052a3;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
